import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export type PlaybookStatus =
  | "active"
  | "paused"
  | "deprecated"
  | "deprecated_on_arrival";

export interface AdminPlaybookRow {
  id: string;
  display_name: string;
  category: string;
  schema_version: number;
  status: PlaybookStatus;
  live_allowed: boolean;
  position_size_override: string | null;
  yaml_sha256: string;
  loaded_at: string | null;
  hypotheses_count_90d: number;
  rolling_hit_rate_90d: string | null;
  rolling_avg_pnl_pct_90d: string | null;
}

export interface AdminPlaybookDetail extends AdminPlaybookRow {
  yaml: Record<string, unknown>;
}

export interface UpdatePlaybookBody {
  status?: PlaybookStatus;
  live_allowed?: boolean;
  position_size_override?: number | null;
}

export const adminPlaybooksKey = ["admin", "playbooks"] as const;
export const adminPlaybookDetailKey = (id: string) =>
  ["admin", "playbooks", id] as const;

export function useAdminPlaybooks() {
  return useQuery<AdminPlaybookRow[]>({
    queryKey: adminPlaybooksKey,
    queryFn: () => apiFetch<AdminPlaybookRow[]>("/admin/playbooks"),
    staleTime: 30_000,
  });
}

export function useAdminPlaybook(id: string | undefined) {
  return useQuery<AdminPlaybookDetail>({
    queryKey: id
      ? adminPlaybookDetailKey(id)
      : ["admin", "playbooks", "__none__"],
    queryFn: () =>
      apiFetch<AdminPlaybookDetail>(`/admin/playbooks/${encodeURIComponent(id ?? "")}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useUpdatePlaybook(id: string) {
  const qc = useQueryClient();
  return useMutation<AdminPlaybookRow, Error, UpdatePlaybookBody>({
    mutationFn: (body) =>
      apiFetch<AdminPlaybookRow>(`/admin/playbooks/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: (updated) => {
      qc.setQueryData<AdminPlaybookRow[]>(adminPlaybooksKey, (prev) => {
        if (!prev) return prev;
        return prev.map((p) => (p.id === id ? updated : p));
      });
      qc.setQueryData<AdminPlaybookDetail | undefined>(
        adminPlaybookDetailKey(id),
        (prev) => (prev ? { ...prev, ...updated } : prev),
      );
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: adminPlaybooksKey });
      await qc.invalidateQueries({ queryKey: adminPlaybookDetailKey(id) });
    },
  });
}
